// react
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { css } from 'styled-components';

// components
import { Button, Card, Icon, Input } from '@/components/atoms';

// tanstack
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Icons } from '@/components/atoms/Icon';

/**
 * Table is a simple table component, using the tanstack react-table.
 */
export const Table = ({
  className,
  columns,
  rows,
  actions,
  defaultAction,
  toolbarActions,
  noDataMsg = 'No data available',
  isSearchable = true,
  isHeader = true,
  isPaginated = true,
  isBorder = false,
  isBorderBottom = false,
  isMobile = false,
  isTotalRows = true,
  rowsPerPage = 6,
}: TableProps) => {
  // state
  const [data, setData] = useState(() => [...(rows || [])]);
  const [filteredData, setFilteredData] = useState(() => [...data]);
  const [searchValue, setSearchValue] = useState('');
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: isPaginated ? rowsPerPage : 100000,
  });

  // memo
  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize],
  );

  const TableRow = useMemo(() => {
    return isMobile ? Card : 'tr';
  }, [isMobile]);

  // hooks/
  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
  });

  // effects
  useEffect(() => {
    setData(rows || []);
  }, [rows]);

  useEffect(() => {
    if (!searchValue) {
      setFilteredData(data);
    } else {
      const filteredData = data.filter((row) => {
        return Object.keys(row).some((key) => {
          if (row[key] === null || row[key] === undefined) return false;
          return row[key]
            .toString()
            .toLowerCase()
            .includes(searchValue.toLowerCase());
        });
      });
      setFilteredData(filteredData);
    }
  }, [data, searchValue]);

  return (
    <TableContainer className={className} $isBorder={isBorder}>
      {isSearchable && (
        <TableToolbar $isMobile={isMobile}>
          <Input
            ariaLabel='search'
            placeholder='Search'
            value={searchValue}
            onChange={setSearchValue}
            iconLeft={'search'}
            size='sm'
          />
          <ToolbarActions>{toolbarActions}</ToolbarActions>
        </TableToolbar>
      )}
      <TableWrapper $isMobile={isMobile}>
        <StyledTable $isMobile={isMobile}>
          <Thead $isMobile={isMobile}>
            {isHeader &&
              table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </th>
                  ))}
                  {actions && actions.length > 0 && <th></th>}
                </tr>
              ))}
          </Thead>
          <Tbody
            $isBorderBottom={isPaginated || isBorderBottom}
            $isMobile={isMobile}
          >
            {table.getRowModel().rows.length === 0 && (
              <tr>
                <td
                  colSpan={table.getHeaderGroups()[0].headers.length}
                  style={{ textAlign: 'center' }}
                >
                  {noDataMsg}
                </td>
              </tr>
            )}
            {table.getRowModel().rows.map((row) => {
              return (
                <TableRow
                  key={row.id}
                  as='tr'
                  color='none'
                  style={{ cursor: 'pointer' }}
                  onClick={() =>
                    !defaultAction?.isHidden?.(row.original) &&
                    defaultAction?.onPress?.(row.original)
                  }
                >
                  {row.getVisibleCells().map((cell) => {
                    let headerContext: any;
                    table.getHeaderGroups().forEach((headerGroup) => {
                      headerGroup.headers.forEach((header) => {
                        if (header.column.id === cell.column.id) {
                          headerContext = header.getContext;
                        }
                      });
                    });

                    const headerContent = flexRender(
                      cell.column.columnDef.header,
                      headerContext(),
                    );

                    const valueContent = flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext(),
                    );

                    return (
                      <td key={cell.id}>
                        <Cell $isMobile={isMobile}>
                          {isMobile && headerContent && (
                            <MobileLabel>{headerContent}</MobileLabel>
                          )}
                          <Value $isMobile={isMobile}>{valueContent}</Value>
                        </Cell>
                      </td>
                    );
                  })}
                  {actions && actions.length > 0 && (
                    <td>
                      <TableActions $isMobile={isMobile}>
                        {actions?.map((action, index) =>
                          !(
                            action.isHidden && action.isHidden(row.original)
                          ) ? (
                            <ActionButton
                              key={index}
                              iconLeft={action.iconLeft}
                              ariaLabel='view'
                              variant={action.buttonVariant || 'secondary'}
                              size={action.buttonSize || 'sm'}
                              onPress={() => action?.onPress(row.original)}
                              $isMobile={isMobile}
                            >
                              {action.label}
                            </ActionButton>
                          ) : null,
                        )}
                      </TableActions>
                    </td>
                  )}
                </TableRow>
              );
            })}
          </Tbody>
          <Tfoot></Tfoot>
        </StyledTable>
      </TableWrapper>
      {isPaginated && (
        <PaginationButtonGroup>
          <PaginationButton
            onClick={() => table.setPageIndex(0)}
            $isDisabled={!table.getCanPreviousPage()}
          >
            <Icon icon='chevrons-left' fontSize={16} />
          </PaginationButton>
          <PaginationButton
            onClick={() => table.previousPage()}
            $isDisabled={!table.getCanPreviousPage()}
          >
            <Icon icon='chevron-left' fontSize={16} />
          </PaginationButton>
          {Array(table.getPageCount())
            .fill(0)
            .map((_, i) => {
              const page = i + 1;
              return (
                <PaginationButton
                  key={i}
                  onClick={() => table.setPageIndex(i)}
                  $isDisabled={table.getState().pagination.pageIndex === i}
                >
                  {page}
                </PaginationButton>
              );
            })}
          <PaginationButton
            onClick={() => table.getCanNextPage() && table.nextPage()}
            $isDisabled={!table.getCanNextPage()}
          >
            <Icon icon='chevron-right' fontSize={16} />
          </PaginationButton>
          <PaginationButton
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            $isDisabled={!table.getCanNextPage()}
          >
            <Icon icon='chevrons-right' fontSize={16} />
          </PaginationButton>
        </PaginationButtonGroup>
      )}
      {isTotalRows && (
        <div>
          {filteredData?.length || 0} row{filteredData?.length !== 1 ? 's' : ''}
        </div>
      )}
    </TableContainer>
  );
};

const TableContainer = styled.div<{ $isBorder?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
  background: ${(props) => props.theme.color.neutral[10]};
  border-radius: 4px;

  ${(props) =>
    props.$isBorder &&
    css`
      border: 1px solid ${props.theme.color.neutral[60]};
      border-radius: 16px;
    `}
`;

const ToolbarActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${(props) => props.theme.spacing[8]};
`;

const TableToolbar = styled.div<MobileProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${(props) => props.theme.spacing[16]};
  background: ${(props) => props.theme.color.neutral[20]};
  padding: ${(props) => props.theme.spacing[12]}
    ${(props) => props.theme.spacing[16]};
  border-radius: 8px;

  ${(props) =>
    props.$isMobile &&
    css`
      flex-direction: column;
    `}
`;

const TableWrapper = styled.div<MobileProps>`
  overflow: auto;
  width: 100%;
  ${(props) =>
    props.$isMobile &&
    css`
      overflow: hidden;
    `}
`;

const StyledTable = styled.table<MobileProps>`
  padding: 0;
  border-spacing: 0;
  overflow-x: auto;
  white-space: nowrap;
  width: 100%;

  ${(props) =>
    props.$isMobile &&
    css`
      table-layout: fixed;
      width: 100%;
      border-spacing: 0 ${props.theme.spacing[24]};
      margin: -${props.theme.spacing[16]} 0;
    `}
`;

interface MobileProps {
  $isMobile?: boolean;
}
const Thead = styled.thead<MobileProps>`
  ${(props) =>
    props.$isMobile &&
    css`
      display: none;
    `}

  & > tr > th {
    margin: 0;
    padding: ${(props) => props.theme.spacing[12]} 0;
    padding-right: ${(props) => props.theme.spacing[32]};
    font: ${(props) => props.theme.typography.body.lg.medium};
    color: ${(props) => props.theme.color.neutral[90]};
    text-align: left;
    border-bottom: 1px solid ${(props) => props.theme.color.neutral[60]};
  }
  & > tr > th:last-child {
    padding-right: 0;
  }
`;

const Tbody = styled.tbody<{ $isBorderBottom?: boolean } & MobileProps>`
  & > tr {
    max-width: 100%;
  }

  & > tr > td {
    ${(props) =>
      props.$isMobile &&
      css`
        display: block;
      `}

    font: ${(props) => props.theme.typography.body.md.regular};
    color: ${(props) => props.theme.color.neutral[80]};
    padding: ${(props) => props.theme.spacing[12]} 0;
    padding-right: ${(props) => props.theme.spacing[32]};

    ${(props) =>
      props.$isBorderBottom &&
      css`
        border-bottom: 1px solid ${(props) => props.theme.color.neutral[50]};
      `}
    ${(props) =>
      props.$isMobile &&
      css`
        border-bottom: none;
        padding: ${(props) => props.theme.spacing[8]} 0;
        border-left: 1px solid ${(props) => props.theme.color.neutral[50]};
        border-right: 1px solid ${(props) => props.theme.color.neutral[50]};
      `}

    ${(props) =>
      props.$isMobile &&
      css`
        padding-left: ${props.theme.spacing[36]};
        padding-right: ${props.theme.spacing[36]};
      `}
  }
  & > tr > td:first-child {
    ${(props) =>
      props.$isMobile &&
      css`
        padding-top: ${props.theme.spacing[36]};
        border-top: 1px solid ${(props) => props.theme.color.neutral[50]};
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
      `}
  }
  & > tr > td:last-child {
    padding-right: 0;

    ${(props) =>
      props.$isMobile &&
      css`
        padding-right: ${props.theme.spacing[36]};
        padding-bottom: ${props.theme.spacing[36]};
        border-bottom: 1px solid ${(props) => props.theme.color.neutral[50]};
        border-bottom-left-radius: 8px;
        border-bottom-right-radius: 8px;
      `}
  }
`;

const Cell = styled.div<MobileProps>`
  ${(props) =>
    props.$isMobile &&
    css`
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: ${(props) => props.theme.spacing[8]};
      padding-bottom: ${(props) => props.theme.spacing[16]};
      border-bottom: 1px solid ${(props) => props.theme.color.neutral[40]};

      width: 100%;
    `}
`;

const MobileLabel = styled.div`
  font: ${(props) => props.theme.typography.body.md.medium};
  color: ${(props) => props.theme.color.neutral[90]};
`;

const Value = styled.div<MobileProps>`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[80]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TableActions = styled.div<MobileProps>`
  display: flex;
  justify-content: flex-end;
  gap: ${(props) => props.theme.spacing[8]};

  ${(props) =>
    props.$isMobile &&
    css`
      justify-content: center;
    `}
`;

const ActionButton = styled(Button)<MobileProps>`
  ${(props) =>
    props.$isMobile &&
    css`
      width: 100%;
      max-width: 300px;
    `}
`;

const Tfoot = styled.tfoot``;

const PaginationButtonGroup = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spacing[2]};
  font: ${(props) => props.theme.typography.button.sm};
  padding: ${(props) => props.theme.spacing[16]};
  padding-top: 0;
`;

interface PaginationButtonProps {
  $isDisabled?: boolean;
}
const PaginationButton = styled.button<PaginationButtonProps>`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;
  min-width: 32px;
  min-height: 32px;
  border-radius: 6px;
  border: 1px solid ${(props) => props.theme.color.neutral[40]};
  color: ${(props) => props.theme.color.neutral[80]};
  cursor: pointer;
  ${(props) =>
    props.$isDisabled &&
    css`
      background: ${props.theme.color.neutral[40]};
    `}
`;

export interface RowAction {
  label: string;
  iconLeft?: Icons;
  isDisabled?: boolean;
  buttonSize?: 'sm' | 'lg';
  buttonVariant?: 'link' | 'secondary';
  isHidden?: (row: any) => boolean;
  onPress: (row: any) => void;
}

interface TableProps {
  className?: string;
  columns: ColumnDef<any, any>[];
  rows?: any[];
  actions?: RowAction[];
  defaultAction?: RowAction;
  toolbarActions?: React.ReactNode;
  noDataMsg?: string;
  isSearchable?: boolean;
  isBorder?: boolean;
  isHeader?: boolean;
  isPaginated?: boolean;
  isMobile?: boolean;
  isBorderBottom?: boolean;
  isTotalRows?: boolean;
  rowsPerPage?: number;
}

Table.displayName = 'Table';

export default Table;
